import { useMemo } from "react";
import ModalShell from "../../../../_components/modal/ModalShell";
import ConfirmDialog from "../../../../_components/modal/ConfirmDialog";
import DynamicForm from "../../../../_components/form/DynamicForm";
import { useCompanyFormSchema } from "./hooks/useCompanyFormSchema";
import { useCompanyFormState } from "./hooks/useCompanyFormState";
import { processCompanyFormSchema } from "./utils/companyFormRules";
import "./_company-form-modal.scss";

/**
 * UI Layer - presentational composition only. Every business decision
 * (schema post-processing, unsaved-changes tracking, submit orchestration,
 * the three mutation sources) lives in ./utils/companyFormRules.js and
 * ./hooks/*; this file just wires their outputs into JSX.
 */
const CompanyFormModal = ({
  isOpen,
  onClose,
  rowData,
  mode,
  onSuccess,
  isSelfServeUser,
  isBillingPortal,
  refetchFilters,
  orgId,
}) => {
  const rawSchema = useCompanyFormSchema(orgId);

  const processedFormSchema = useMemo(
    () => processCompanyFormSchema(rawSchema, { isBillingPortal }),
    [rawSchema, isBillingPortal]
  );

  const {
    control,
    formData,
    errors,
    handleChange,
    handleBlur,
    setImageFile,
    setShouldDeleteImage,
    showCloseConfirmation,
    isSubmitting,
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    onSubmit,
  } = useCompanyFormState(processedFormSchema, {
    mode,
    rowData,
    isOpen,
    isSelfServeUser,
    onClose,
    onSuccess,
    refetchFilters,
  });

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onRequestClose={attemptClose}
        className="company-form-modal--right-docked"
        width="500px"
        height="100vh"
        center={false}
        right={0}
        title={mode === "edit" ? "Edit company" : "Create company"}
        description={
          mode === "edit"
            ? "Updates this account's record."
            : "Creates an account record. Billing details can follow later."
        }
        isSubmitting={isSubmitting}
        footerButtons={[
          { children: "Close", onClick: attemptClose, variant: "outlined" },
          {
            children: mode === "edit" ? "Update" : "Create",
            onClick: onSubmit,
            variant: "contained",
            loading: isSubmitting,
          },
        ]}
      >
        <form onSubmit={onSubmit}>
          <DynamicForm
            schema={processedFormSchema}
            control={control}
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            mode={mode}
            setImageFile={setImageFile}
            setShouldDeleteImage={setShouldDeleteImage}
            disabled={isSubmitting}
          />
        </form>
      </ModalShell>

      <ConfirmDialog
        isOpen={showCloseConfirmation}
        title="Discard unsaved changes?"
        description="You have unsaved changes. Are you sure you want to close without saving?"
        cancelText="Keep editing"
        confirmText="Discard changes"
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
      />
    </>
  );
};

export default CompanyFormModal;
